/* Recover a Klaviyo exit animation whose completion callback was lost. */
(() => {
  const selector = '[data-kl-scroll-locking-modal="true"]';
  const dismissed = 'data-pd-popup-dismissed';
  const lock = 'klaviyo-prevent-body-scrolling';
  const records = new Map();
  let queued;
  let lastOutsideFocus;
  let releasePending = false;

  const isClosing = (style) => style.animationName.split(',').some((name) => name.trim() === 'klaviyo-fadeout');
  const seconds = (value) => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
  const grace = (style) => Math.min(10000, Math.max(750,
    ...style.animationDuration.split(',').map((duration, index) =>
      (seconds(duration) || 0) + (seconds(style.animationDelay.split(',')[index] || '0s') || 0) + 200)));

  function releaseOrphanedLock() {
    if (!releasePending) return;
    const active = [...document.querySelectorAll(selector)].some((modal) => {
      if (modal.hasAttribute(dismissed)) return false;
      const style = getComputedStyle(modal);
      // An entering popup can be fully transparent and must still own its lock.
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    if (!active) {
      document.body.classList.remove(lock);
      releasePending = false;
    }
  }

  function checkDismissal(modal, record) {
    clearTimeout(record.timer);
    record.timer = undefined;
    if (record.opening) return;
    if (!modal.isConnected || records.get(modal) !== record) {
      releasePending = true;
      releaseOrphanedLock();
      return;
    }
    const style = getComputedStyle(modal);
    record.checked = true;
    record.checks += 1;
    if (isClosing(style) && Number(style.opacity) <= 0.001 && style.display !== 'none') {
      // Leave provider DOM and inline styles intact so Klaviyo can reuse the form.
      modal.setAttribute(dismissed, '');
      releasePending = true;
      if (modal.contains(document.activeElement)) {
        if (lastOutsideFocus?.isConnected) lastOutsideFocus.focus({ preventScroll: true });
        else document.activeElement?.blur();
      }
    } else if (style.display === 'none') releasePending = true;
    else if (isClosing(style) && record.checks < 3) {
      record.timer = setTimeout(() => checkDismissal(modal, record), 750);
    }
    releaseOrphanedLock();
  }

  function reconcile() {
    queued = undefined;
    for (const [modal, record] of records) {
      if (!modal.isConnected) {
        clearTimeout(record.timer);
        records.delete(modal);
        releasePending = true;
      }
    }
    for (const modal of document.querySelectorAll(selector)) {
      let record = records.get(modal);
      if (!record) {
        record = { closing: false, opening: false, checked: false, checks: 0, timer: undefined };
        records.set(modal, record);
      }
      const style = getComputedStyle(modal);
      if (!isClosing(style)) {
        clearTimeout(record.timer);
        record.timer = undefined;
        record.closing = false;
        record.opening = false;
        record.checked = false;
        record.checks = 0;
        modal.removeAttribute(dismissed);
      } else if (!record.closing && !record.opening) {
        record.closing = true;
        record.timer = setTimeout(() => checkDismissal(modal, record), grace(style));
      } else if (record.checked && !record.opening && record.timer === undefined && !modal.hasAttribute(dismissed)) {
        checkDismissal(modal, record);
      }
    }
    releaseOrphanedLock();
  }

  function schedule() {
    if (queued === undefined) queued = setTimeout(reconcile, 0);
  }

  function resume() {
    // Timers/animation events can be suspended while an in-app tab is backgrounded.
    for (const record of records.values()) {
      clearTimeout(record.timer);
      record.timer = undefined;
      record.closing = false;
      record.checked = false;
      record.checks = 0;
    }
    schedule();
  }

  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        return mutation.target.matches(selector);
      }
      return [...mutation.addedNodes, ...mutation.removedNodes].some((node) =>
        node.nodeType === 1 && (node.matches(selector) || node.querySelector(selector)));
    });
    if (relevant) schedule();
  });
  observer.observe(document.body, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['style', 'class', 'data-kl-scroll-locking-modal']
  });

  document.addEventListener('focusin', (event) => {
    if (!event.target.closest(selector)) lastOutsideFocus = event.target;
  });
  window.addEventListener('klaviyoForms', (event) => {
    const { type, formId } = event.detail || {};
    if (type !== 'open' && type !== 'close') return;
    for (const [modal, record] of records) {
      if (modal.querySelector('.klaviyo-form')?.getAttribute('data-testid') !== `klaviyo-form-${formId}`) continue;
      clearTimeout(record.timer);
      record.timer = undefined;
      record.closing = false;
      record.checked = false;
      record.checks = 0;
      record.opening = type === 'open';
      if (record.opening) modal.removeAttribute(dismissed);
    }
    schedule();
  });
  document.addEventListener('animationend', (event) => {
    const record = records.get(event.target);
    if (record && event.animationName === 'klaviyo-fadeout') checkDismissal(event.target, record);
  });
  window.addEventListener('pageshow', resume);
  window.addEventListener('focus', resume);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resume();
  });
  reconcile();
})();
